/**
 * Owner-only confirmation before `DELETE /workspaces/{id}` (Figma 9397:7027).
 *
 * The destructive button stays disabled until the user types the workspace
 * name exactly; the parent performs the request and reports `busy` so the
 * footer locks while the cascade runs.
 */
import React, { useEffect, useState } from 'react';
import { Input, Modal, SparkIcon, SparkModalPortal } from '@spark';

export const DELETE_WORKSPACE_MODAL_WIDTH = 600;
export const DELETE_WORKSPACE_WARNING =
    'Deleting this workspace will permanently erase all deployed SLMs, datasets, and all other data. ' +
    'Additionally, any live API endpoints will break, and all connected users will immediately lose access ' +
    'to this workspace.';

/** True when the typed confirmation matches the workspace name (surrounding whitespace ignored). */
export function confirmationMatches(typed, workspaceName) {
    return String(typed ?? '').trim() === String(workspaceName ?? '').trim() && String(workspaceName ?? '').trim() !== '';
}

export default function DeleteWorkspaceModal({ open, workspaceName, busy = false, onClose, onConfirm }) {
    const [typed, setTyped] = useState('');

    useEffect(() => {
        if (open) setTyped('');
    }, [open]);

    const canDelete = confirmationMatches(typed, workspaceName) && !busy;
    const close = () => {
        if (busy) return;
        onClose?.();
    };
    const confirm = () => {
        if (!canDelete) return;
        onConfirm?.();
    };

    return (
        <SparkModalPortal open={open} onClose={close} width={DELETE_WORKSPACE_MODAL_WIDTH} destroyOnClose>
            <Modal
                open
                danger
                className="workspace-danger-modal delete-workspace-modal"
                title={
                    <span className="workspace-danger-modal__title">
                        <SparkIcon slug="trash" size="medium" />
                        {`Are you sure you want to delete ${workspaceName}?`}
                    </span>
                }
                showSubtitle={false}
                showOverlay={false}
                showScrollBar={false}
                fixedHeight={false}
                primaryLabel="Delete Workspace"
                primaryIcon={busy ? <SparkIcon slug="loader" size="small" className="spark-icon-spin" /> : undefined}
                primaryDisabled={!canDelete}
                tertiaryLabel="Cancel"
                tertiaryDisabled={busy}
                onPrimaryClick={confirm}
                onTertiaryClick={close}
                onCloseClick={close}
            >
                <form
                    className="workspace-danger-modal__body"
                    onSubmit={(event) => {
                        event.preventDefault();
                        confirm();
                    }}
                >
                    <p className="workspace-danger-modal__text">
                        This action is permanent and cannot be undone.
                        <br />
                        {DELETE_WORKSPACE_WARNING}
                    </p>
                    <Input
                        id="delete-workspace-confirmation"
                        className="delete-workspace-modal__input"
                        label={
                            <>
                                To delete forever, please type <strong>{workspaceName}</strong>
                            </>
                        }
                        size="large"
                        placeholder={workspaceName}
                        autoComplete="off"
                        value={typed}
                        disabled={busy}
                        onChange={(event) => setTyped(event.target.value)}
                    />
                </form>
            </Modal>
        </SparkModalPortal>
    );
}
